import { fDateNumericTimeNumeric } from '@faks/shared';
import { select, t } from '@lingui/core/macro';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Grow,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
  Zoom,
} from '@mui/material';
import { useDestroyMutation, useIndexQuery } from 'api';
import { CloseIcon } from 'components/icons/CloseIcon';
import { DownloadIcon } from 'components/icons/DownloadIcon';
import { Spinner } from 'components/Spinner';
import { useCurrentUserAndValidateRoles } from 'hooks/slices';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { TrackedActions, useTrackEvent } from 'utils/tracking';

export const ExportCenter = () => {
  const [isOpen, setIsOpen] = useState(true);

  const theme = useTheme();
  const trackEvent = useTrackEvent();
  const { currentUser } = useCurrentUserAndValidateRoles() || {};

  const { data: exportList, refetch } = useIndexQuery(
    'tech/exports',
    {},
    {
      refetchInterval: (query) => {
        const exports = query.state.data?.exports;
        if (!exports || exports.length === 0) return false;
        if (exports.some((e) => e.status === 'pending' || e.status === 'processing')) return 2000;
        return false;
      },
      enabled: !!currentUser?.id,
    }
  );

  const destroyExportMutation = useDestroyMutation('tech/exports');
  const destroyFinishedMutation = useDestroyMutation('tech/exports/_destroy_finished');

  const exports = exportList?.exports;

  const prevFailedIds = useRef<Set<number> | null>(null);
  useEffect(() => {
    if (exports) {
      const currentFailedIds = new Set(
        exports.filter((e) => e.status === 'failed').map((e) => e.id)
      );
      if (prevFailedIds.current !== null) {
        exports.forEach((e) => {
          if (e.status === 'failed' && !prevFailedIds.current?.has(e.id)) {
            toast.error(
              t`L'export a échoué. Veuillez réessayer plus tard. Si le problème persiste, veuillez contacter le support.`
            );
          }
        });
      }
      prevFailedIds.current = currentFailedIds;
    }
  }, [exports]);

  const hasFinishedExports = exports?.some((e) => e.status === 'finished') ?? false;

  const handleDelete = (exportId: number) => {
    destroyExportMutation.mutateAsync(exportId, { onSuccess: () => refetch() });
  };

  const handleClearAllExports = () => {
    destroyFinishedMutation.mutateAsync(undefined, { onSuccess: () => refetch() });
  };

  if (!currentUser?.id) return null;

  if (!exports || exports.length === 0) return null;

  return (
    <>
      <Zoom in={!isOpen} unmountOnExit mountOnEnter>
        <Box sx={{ position: 'fixed', top: 5, right: 5, zIndex: 1300 }}>
          <Tooltip title={t`Mes exports`} placement="left">
            <Button
              onClick={() => setIsOpen(true)}
              color="primary"
              variant="contained"
              sx={{ borderRadius: '50%', width: '36px', height: '36px', minWidth: 'auto' }}
            >
              <Badge variant="dot" color="error">
                <DownloadIcon fontSize={20} />
              </Badge>
            </Button>
          </Tooltip>
        </Box>
      </Zoom>

      <Grow in={isOpen} style={{ transformOrigin: 'top right' }} unmountOnExit mountOnEnter>
        <Card
          sx={{
            position: 'fixed',
            top: 16,
            right: 16,
            width: 300,
            zIndex: 1300,
            backgroundColor: theme.palette.info.lighter,
            boxShadow: 24,
          }}
        >
          <CardContent sx={{ py: '17px !important' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="h6">{t`Mes exports`}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {hasFinishedExports && (
                  <Button
                    onClick={handleClearAllExports}
                    disabled={destroyFinishedMutation.isPending}
                    sx={{ fontSize: '0.7rem' }}
                    size="small"
                  >
                    {t`Tout effacer`}
                  </Button>
                )}
                <Button sx={{ minWidth: 'auto' }} onClick={() => setIsOpen(false)}>
                  <CloseFullscreenIcon />
                </Button>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'auto 1fr auto',
                columnGap: 1,
                rowGap: 0.5,
                alignItems: 'center',
                mt: 1,
              }}
            >
              <Typography
                variant="caption"
                fontWeight="bold"
                sx={{ color: theme.palette.text.secondary }}
              >
                {t`Statut`}
              </Typography>
              <Typography
                variant="caption"
                fontWeight="bold"
                sx={{
                  color: theme.palette.text.secondary,
                  textAlign: 'center',
                }}
              >
                {t`Date`}
              </Typography>
              <span />
              {exports?.map((exportFile) => (
                <Box key={exportFile.id} sx={{ display: 'contents' }}>
                  <Typography
                    variant="caption"
                    sx={{
                      whiteSpace: 'nowrap',
                      color: exportFile.status === 'failed' ? theme.palette.error.main : undefined,
                    }}
                  >
                    {select(exportFile.status, {
                      finished: 'Terminé',
                      processing: 'En cours',
                      pending: 'En cours',
                      failed: 'Échoué',
                      other: exportFile.status,
                    })}
                  </Typography>
                  <Typography variant="caption" sx={{ textAlign: 'center' }}>
                    {fDateNumericTimeNumeric(exportFile.created_at)}
                  </Typography>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      gap: 0.5,
                    }}
                  >
                    {exportFile.status === 'finished' && (
                      <Tooltip title={t`Télécharger`}>
                        <Button
                          href={exportFile.file_url}
                          onClick={() =>
                            trackEvent(TrackedActions.Other.downloadExport, { id: exportFile.id })
                          }
                          sx={{ minWidth: 'auto', padding: 0.5 }}
                        >
                          <DownloadIcon sx={{ fontSize: 24 }} />
                        </Button>
                      </Tooltip>
                    )}
                    {(exportFile.status === 'pending' || exportFile.status === 'processing') && (
                      <Spinner size={22} />
                    )}
                    <Tooltip title={t`Supprimer`}>
                      <IconButton
                        onClick={() => handleDelete(exportFile.id)}
                        disabled={destroyExportMutation.isPending}
                        size="small"
                        sx={{ padding: 0.25 }}
                      >
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>
      </Grow>
    </>
  );
};
